import { AriaAttributes, forwardRef, ReactNode, Ref } from 'react';

import { Divider } from '@mui/material';

import clsx from 'clsx';

import { omit } from '../../utils/helpers';
import Placeholder from './Placeholder';
import { Children, Root, sectionClasses, Title } from './styles';
import useSection from './vm';

export interface Props extends AriaAttributes {
  title: string | undefined | null | ((titleProps: { id: string; 'aria-hidden': boolean }) => ReactNode);
  children: ReactNode;
  /** @default 'default' */
  variant?: 'default' | 'transparent' | 'emphasized';
  className?: string;
  classes?: Partial<Record<keyof typeof sectionClasses, string>>;
  tabIndex?: number;
}

export { sectionClasses };

const Section = forwardRef(
  (
    { title, children, variant = 'default', className, classes, ...props }: Props,
    ref: Ref<HTMLDivElement>
  ) => {
    const { titleId } = useSection();

    const ariaHidden = props['aria-hidden'];
    const ariaLabel = props['aria-label'];
    const ariaLabelledby = props['aria-labelledby'];

    return (
      <Root
        ref={ref}
        className={clsx(
          className,
          {
            [sectionClasses.variantDefault]: variant === 'default',
            [sectionClasses.variantTransparent]: variant === 'transparent',
            [sectionClasses.variantEmphasized]: variant === 'emphasized',
          },
          classes?.root,
          classes?.variantDefault ? { [classes.variantDefault]: variant === 'default' } : undefined,
          classes?.variantTransparent
            ? { [classes.variantTransparent]: variant === 'transparent' }
            : undefined,
          classes?.variantEmphasized ? { [classes.variantEmphasized]: variant === 'emphasized' } : undefined
        )}
        divider={variant === 'default' ? <Divider /> : null}
        component="section"
        {...omit(props, 'aria-hidden', 'aria-label', 'aria-labelledby')}
        {...(ariaHidden
          ? { 'aria-hidden': true }
          : ariaLabel
          ? { 'aria-label': ariaLabel }
          : { 'aria-labelledby': ariaLabelledby || titleId })}
      >
        {typeof title === 'string' ? (
          <Title
            id={titleId}
            variant={variant === 'default' ? 'h6Bold' : 'h5Bold'}
            component="div"
            aria-hidden
            className={clsx(sectionClasses.title, classes?.title)}
          >
            {title}
          </Title>
        ) : typeof title === 'function' ? (
          title({ id: titleId, 'aria-hidden': true })
        ) : null}
        {typeof children === 'string' ? (
          variant === 'transparent' ? (
            <Placeholder className={clsx(sectionClasses.children, classes?.children)}>{children}</Placeholder>
          ) : (
            <Children className={clsx(sectionClasses.children, classes?.children)}>{children}</Children>
          )
        ) : (
          children
        )}
      </Root>
    );
  }
);

export default Section;
